import React, { useState, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { toast } from 'react-toastify';
import { 
    IoAdd, IoTrash, IoSave, IoMail, IoCheckmarkCircle, 
    IoPauseCircle, IoRefresh, IoChevronDown, IoChevronForward,
    IoStatsChart, IoTime, IoAlertCircle, IoSettings, IoSync,
    IoCreateOutline, IoChatbubblesOutline, IoNotificationsOutline, 
    IoConstructOutline, IoInformationCircleOutline
} from 'react-icons/io5';
import '@/styles/EmailMailboxesStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * EmailMailboxesPage - Manage email mailbox configurations for inbound email processing
 * 
 * Features:
 * - View all configured email mailboxes
 * - Create/Edit/Delete mailbox configurations
 * - View processing statistics
 * - View processed email history
 * - Toggle mailbox active status
 */
const EmailMailboxesPage = () => {
    const { apiFetch, user } = useAuth();
    const [mailboxes, setMailboxes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editMailbox, setEditMailbox] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [statsMailbox, setStatsMailbox] = useState(null);
    const [stats, setStats] = useState(null);
    const [processedEmails, setProcessedEmails] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);
    const [expandedSection, setExpandedSection] = useState('processed'); // Controls which section is expanded in stats modal

    // Alias management state
    const [aliases, setAliases] = useState([]);
    const [editAlias, setEditAlias] = useState(null); // alias being edited or a new blank alias
    const [savingAlias, setSavingAlias] = useState(false);

    // Lookup data
    const [actionItemTypes, setActionItemTypes] = useState([]);
    const [emailConfigSecrets, setEmailConfigSecrets] = useState([]);
    const [properties, setProperties] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [itTicketTypes, setItTicketTypes] = useState([]);

    const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
    const providerOptions = [
        { value: 'Microsoft365', label: 'Microsoft 365 (Graph API)' },
        { value: 'IMAP', label: 'IMAP (Traditional Mail Server)' }
    ];
    const functionalAreaOptions = [
        { value: 'General', label: 'General (Notifications & Reports)' },
        { value: 'ITServiceManagement', label: 'IT Service Management' },
        { value: 'ActionItems', label: 'Action Items' },
        { value: 'AssetManagement', label: 'Asset Management' },
        { value: 'Custom', label: 'Custom' }
    ];
    const replyActionOptions = [
        { value: 'AddComment', label: 'Add as comment to existing item' },
        { value: 'CreateNew', label: 'Create a new item' }
    ];

    /**
     * Inbound mode options — controls how the mailbox handles incoming emails.
     * This is the core architectural concept that replaces the simple InboundEnabled boolean.
     */
    const inboundModeOptions = [
        { 
            value: 'CreateAndReply', 
            label: 'Create & Reply',
            icon: <IoCreateOutline size={20} />,
            description: 'Creates new action items from incoming emails AND processes replies to existing threads',
            useCases: 'IT Helpdesk, Support Inbox, CC Tracking, General Inquiry'
        },
        { 
            value: 'ReplyOnly', 
            label: 'Reply Only',
            icon: <IoChatbubblesOutline size={20} />,
            description: 'Only processes replies to existing action item threads — standalone emails are ignored',
            useCases: 'SIPA Notifications, Asset Management, Project Updates, Contract Management'
        },
        { 
            value: 'Disabled', 
            label: 'Outbound Only',
            icon: <IoNotificationsOutline size={20} />,
            description: 'No inbound processing — this mailbox is only used for sending notifications',
            useCases: 'System Alerts, Bulk Notifications, One-way Communication'
        }
    ];

    /**
     * Purpose presets for quick-start mailbox configuration.
     * Shown when creating a new mailbox to guide the user.
     */
    const mailboxPresets = [
        {
            id: 'helpdesk',
            label: 'Helpdesk / Support Inbox',
            icon: <IoMail size={28} />,
            description: 'Receive new requests via email, create tickets, and process replies.',
            examples: 'IT Support, Facilities, HR Requests',
            defaults: {
                inboundMode: 'CreateAndReply',
                functionalArea: 'ITServiceManagement',
                outboundEnabled: true,
                autoResponseEnabled: true,
                actionItemType: 'Ad-Hoc',
                defaultPriority: 'Medium',
                replyAction: 'AddComment'
            }
        },
        {
            id: 'notifications',
            label: 'Notification & Reply Channel',
            icon: <IoChatbubblesOutline size={28} />,
            description: 'Send notifications from a module and process email replies to update items.',
            examples: 'SIPA Updates, Asset Management, Project Management, Contract Expiry',
            defaults: {
                inboundMode: 'ReplyOnly',
                functionalArea: 'General',
                outboundEnabled: true,
                autoResponseEnabled: false,
                replyAction: 'AddComment'
            }
        },
        {
            id: 'cctracking',
            label: 'CC Tracking Inbox',
            icon: <IoCreateOutline size={28} />,
            description: 'Create action items when users CC this address on emails.',
            examples: 'Work Tracking, Ad-Hoc Task Creation, Email-to-Task',
            defaults: {
                inboundMode: 'CreateAndReply',
                functionalArea: 'ActionItems',
                outboundEnabled: false,
                autoResponseEnabled: true,
                actionItemType: 'Ad-Hoc',
                defaultPriority: 'Medium',
                replyAction: 'AddComment'
            }
        },
        {
            id: 'custom',
            label: 'Custom Configuration',
            icon: <IoConstructOutline size={28} />,
            description: 'Configure all options manually for advanced use cases.',
            examples: 'Specialized workflows, unique routing requirements',
            defaults: {}
        }
    ];

    /**
     * Known action item type descriptions for the dropdown tooltip.
     * Types are pulled dynamically from existing action items in the database,
     * so this map provides context for each known type.
     */
    const actionItemTypeDescriptions = {
        'Ad-Hoc': {
            description: 'General-purpose action items created on demand. Best for email inboxes that receive varied requests without a specific workflow.',
            recommended: true
        },
        'Alert': {
            description: 'System-generated alert notifications. Typically created automatically by monitoring or scheduled tasks — not usually appropriate for inbound email processing.',
            recommended: false
        },
        'Contract Expiry': {
            description: 'Items related to expiring contracts requiring follow-up. Only use if this mailbox specifically handles contract renewal or lease expiry communications.',
            recommended: false
        },
        'ContractExpiry': {
            description: 'Items related to expiring contracts requiring follow-up. Only use if this mailbox specifically handles contract renewal or lease expiry communications.',
            recommended: false
        },
        'DailyChecklist': {
            description: 'Items generated from daily operational checklists. These are normally created automatically by the checklist system — not recommended for email inboxes.',
            recommended: false
        },
        'Non-Compliance': {
            description: 'Items from non-compliance inspections or reports. Use if this mailbox receives non-compliance notifications that should be tracked as formal action items.',
            recommended: false
        },
        'ProjectTask': {
            description: 'Task items linked to project management workflows, with Gantt chart and dependency support. Use if emails should create project-related tasks.',
            recommended: false
        },
        'SIPA': {
            description: 'Items from SIPA (Safety Inspections & Property Audits) forms. These are normally created by the SIPA inspection system — not recommended for general email inboxes.',
            recommended: false
        },
        'IT': {
            description: 'IT Service Management — triggers ITSM features including SLA tracking, ticket references, and CSAT surveys. Select a specific ticket category after choosing this type.',
            recommended: true
        },
        // IT ticket type descriptions are merged dynamically from the API below
        ...Object.fromEntries(itTicketTypes.map(t => [t.typeKey, {
            description: t.description || `${t.displayName} — triggers ITSM features (SLA, ticket reference, CSAT).`,
            recommended: true
        }])),
        'TestTemplate': {
            description: '⚠️ Test/development template used for testing purposes only. Should not be used for production mailboxes.',
            recommended: false
        }
    };

    /**
     * Get description info for an action item type.
     * Returns the known description or a generic one for unknown types.
     */
    const getActionItemTypeInfo = (type) => {
        if (!type) return null;
        return actionItemTypeDescriptions[type] || {
            description: `Custom type "${type}" — items created from this mailbox will be classified under this type. Verify this is the intended classification for email-generated action items.`,
            recommended: null
        };
    };

    // Email templates for outbound/auto-response selectors
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [functions, setFunctions] = useState([]);
    const [boards, setBoards] = useState([]);

    // Mail folders fetched from the mailbox's email provider
    const [mailFolders, setMailFolders] = useState([]);
    const [loadingFolders, setLoadingFolders] = useState(false);

    /**
     * Fetch all mailboxes from the API
     */
    const fetchMailboxes = async () => {
        try {
            const response = await apiFetch('/api/EmailMailboxes');
            const data = await response.json();
            if (data.success) {
                setMailboxes(data.data);
            } else {
                toast.error(data.message || 'Failed to load mailboxes');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Failed to load mailboxes');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch mail folders from the mailbox's email provider (Microsoft 365).
     * Only works for saved mailboxes with a valid email config secret.
     */
    const fetchMailFolders = async (mailboxId) => {
        if (!mailboxId) {
            setMailFolders([]);
            return;
        }
        setLoadingFolders(true);
        try {
            const response = await apiFetch(`/api/EmailMailboxes/${mailboxId}/folders`);
            const data = await response.json();
            if (data.success) {
                setMailFolders(data.data);
            } else {
                setMailFolders([]);
            }
        } catch (e) {
            Sentry.captureException(e);
            setMailFolders([]);
        } finally {
            setLoadingFolders(false);
        }
    };

    /**
     * Fetch action item types for dropdown
     */
    const fetchActionItemTypes = async () => {
        try {
            const response = await apiFetch('/api/EmailMailboxes/action-item-types');
            const data = await response.json();
            if (data.success) {
                setActionItemTypes(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetch email config secrets for dropdown
     */
    const fetchEmailConfigSecrets = async () => {
        try {
            const response = await apiFetch('/api/AppSecrets/by-category');
            const data = await response.json();
            if (data.success) {
                // Find secrets of type EmailConfig (exact match only)
                const allSecrets = [];
                Object.values(data.data).forEach(secrets => {
                    secrets.forEach(s => {
                        if (s.secretType === 'EmailConfig') {
                            allSecrets.push(s);
                        }
                    });
                });
                setEmailConfigSecrets(allSecrets);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetch properties for dropdown
     */
    const fetchProperties = async () => {
        try {
            const response = await apiFetch('/api/property/all');
            const data = await response.json();
            if (data.success) {
                setProperties(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetch departments for dropdown
     */
    const fetchDepartments = async () => {
        try {
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            if (data.success) {
                setDepartments(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    /**
     * Fetch users for dropdown
     */
    const fetchUsers = async () => {
        try {
            const response = await apiFetch('/api/user/all');
            const data = await response.json();
            if (data.success) {
                setUsers(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const response = await apiFetch('/api/EmailTemplates');
            const data = await response.json();
            if (data.success) {
                setEmailTemplates(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchFunctions = async () => {
        try {
            const response = await apiFetch('/api/Functions');
            const data = await response.json();
            if (data.success) {
                setFunctions(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchBoards = async () => {
        try {
            const response = await apiFetch('/api/KanbanBoards');
            const data = await response.json();
            if (data.success) {
                setBoards(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchItTicketTypes = async () => {
        try {
            const response = await apiFetch('/api/ITTicketTypes');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setItTicketTypes(data.data);
            } else {
                setItTicketTypes(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    useEffect(() => {
        fetchMailboxes();
        fetchActionItemTypes();
        fetchItTicketTypes();
        fetchEmailConfigSecrets();
        fetchProperties();
        fetchDepartments();
        fetchUsers();
        fetchEmailTemplates();
        fetchFunctions();
        fetchBoards();
    }, []);

    /**
     * Handle creating a new mailbox — show preset selector first
     */
    const handleCreateClick = () => {
        setEditMailbox(null);
        setIsCreateMode(true);
    };

    /**
     * Apply a preset and initialize the mailbox form
     */
    const handlePresetSelect = (preset) => {
        const defaultActionItemType = actionItemTypes.length > 0 ? actionItemTypes[0] : '';

        const newMailbox = {
            displayName: '',
            emailAddress: '',
            provider: 'Microsoft365',
            isActive: true,
            functionalArea: 'General',
            purpose: '',
            inboundEnabled: true,
            inboundMode: 'CreateAndReply',
            actionItemType: defaultActionItemType,
            templateKey: '',
            replyAction: 'AddComment',
            defaultPropertyId: null,
            defaultDepartmentId: null,
            defaultAssignToUserId: null,
            defaultFunctionId: null,
            defaultPriority: 'Medium',
            defaultTicketCategoryId: null,
            outboundEnabled: false,
            outboundTemplateId: null,
            outboundFromName: '',
            autoResponseEnabled: false,
            autoResponseTemplateId: null,
            autoResponseSubject: '',
            imapServer: '',
            imapPort: 993,
            imapUseSsl: true,
            imapUsername: '',
            imapPassword: '',
            emailConfigSecretId: null,
            folderToMonitor: 'INBOX',
            processedFolder: '',
            markAsReadAfterProcessing: true,
            pollingIntervalMinutes: null,
            ...preset.defaults
        };

        // Derive InboundEnabled from InboundMode
        newMailbox.inboundEnabled = newMailbox.inboundMode !== 'Disabled';

        setEditMailbox(newMailbox);
    };

    /**
     * Handle clicking on a mailbox to edit
     */
    const handleMailboxClick = async (mailbox) => {
        try {
            const response = await apiFetch(`/api/EmailMailboxes/${mailbox.id}`);
            const data = await response.json();
            if (data.success) {
                setEditMailbox(data.data);
                setIsCreateMode(false);
                // Load aliases for existing mailboxes
                setAliases(data.data.aliases || []);
                setEditAlias(null);
                // Fetch mail folders for folder dropdowns
                fetchMailFolders(data.data.id);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Failed to load mailbox details');
        }
    };

    /**
     * Close the edit modal
     */
    const closeModal = () => {
        setEditMailbox(null);
        setIsCreateMode(false);
        setSaving(false);
        setAliases([]);
        setEditAlias(null);
        setMailFolders([]);
    };

    /**
     * Handle field changes in the form
     */
    const handleFieldChange = (field, value) => {
        setEditMailbox(prev => {
            const updated = { ...prev, [field]: value };
            // Keep InboundEnabled in sync with InboundMode
            if (field === 'inboundMode') {
                updated.inboundEnabled = value !== 'Disabled';
            }
            return updated;
        });
    };

    /**
     * Save the mailbox (create or update)
     */
    const handleSave = async () => {
        if (!editMailbox.emailAddress || !editMailbox.emailAddress.trim()) {
            toast.error("Email address is required.");
            return;
        }

        if (editMailbox.inboundMode === 'CreateAndReply' && (!editMailbox.actionItemType || !editMailbox.actionItemType.trim())) {
            toast.error("Action item type is required when inbound mode is 'Create & Reply'.");
            return;
        }

        setSaving(true);
        try {
            const method = isCreateMode ? 'POST' : 'PUT';
            const url = isCreateMode ? '/api/EmailMailboxes' : `/api/EmailMailboxes/${editMailbox.id}`;

            const payload = {
                ...editMailbox,
                userId: user?.id
            };

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (response.ok && data.success) {
                toast.success(isCreateMode ? 'Mailbox created successfully' : 'Mailbox updated successfully');
                closeModal();
                fetchMailboxes();
            } else {
                toast.error(data?.message || 'Failed to save mailbox');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error saving mailbox');
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Toggle mailbox active status
     */
    const handleToggleActive = async (mailbox, event) => {
        event.stopPropagation();
        try {
            const response = await apiFetch(`/api/EmailMailboxes/${mailbox.id}/toggle-active`, {
                method: 'POST'
            });
            const data = await response.json();
            if (data.success) {
                toast.success(data.message);
                fetchMailboxes();
            } else {
                toast.error(data.message || 'Failed to toggle status');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Failed to toggle mailbox status');
        }
    };

    /**
     * Handle delete confirmation
     */
    const handleDeleteClick = (mailbox, event) => {
        event.stopPropagation();
        setDeleteTarget(mailbox);
    };

    /**
     * Confirm delete
     */
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            const response = await apiFetch(`/api/EmailMailboxes/${deleteTarget.id}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Mailbox deleted successfully');
                fetchMailboxes();
            } else {
                toast.error(data.message || 'Failed to delete mailbox');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error deleting mailbox');
            console.error(e);
        } finally {
            setDeleteTarget(null);
        }
    };

    /**
     * View mailbox statistics
     */
    const handleViewStats = async (mailbox, event) => {
        event.stopPropagation();
        setStatsMailbox(mailbox);
        setLoadingStats(true);
        setStats(null);
        setProcessedEmails([]);

        try {
            // Fetch stats and processed emails in parallel
            const [statsResponse, emailsResponse] = await Promise.all([
                apiFetch(`/api/EmailMailboxes/${mailbox.id}/stats`),
                apiFetch(`/api/EmailMailboxes/${mailbox.id}/processed-emails?limit=25`)
            ]);

            const [statsData, emailsData] = await Promise.all([
                statsResponse.json(),
                emailsResponse.json()
            ]);

            if (statsData.success) {
                setStats(statsData.data);
            }

            if (emailsData.success) {
                setProcessedEmails(emailsData.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
            toast.error('Failed to load statistics');
        } finally {
            setLoadingStats(false);
        }
    };

    /**
     * Close stats modal
     */
    const closeStatsModal = () => {
        setStatsMailbox(null);
        setStats(null);
        setProcessedEmails([]);
    };

    /**
     * Trigger an on-demand inbound email check for a single mailbox
     */
    const handleTriggerCheck = async (mailbox, e) => {
        e.stopPropagation();
        try {
            toast.info(`Checking ${mailbox.displayName || mailbox.emailAddress}...`);
            const res = await apiFetch(`/api/EmailMailboxes/${mailbox.id}/trigger-check`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success('Mailbox check completed successfully');
            } else {
                toast.warning(data.message || 'Check completed with errors');
            }
            await fetchMailboxes();
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to trigger mailbox check');
        }
    };

    /**
     * Format date for display — "20 April 2026, 14:59:06" in local timezone.
     * Backend stores UTC; we append 'Z' if missing so the browser converts correctly.
     */
    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        const raw = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
        const d = new Date(raw);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
            + ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    };

    /**
     * Get status badge class
     */
    const getResultBadgeClass = (result) => {
        switch (result) {
            case 'CreatedActionItem': return 'success';
            case 'AddedComment': return 'info';
            case 'Error': return 'error';
            case 'Skipped': return 'skipped';
            default: return 'default';
        }
    };

    /**
     * Build a dynamic behavior summary based on current mailbox configuration
     */
    const getBehaviorSummary = () => {
        if (!editMailbox) return [];
        const items = [];
        const mode = editMailbox.inboundMode || 'CreateAndReply';

        if (mode === 'CreateAndReply') {
            items.push({ icon: '✅', text: `Creates new [${editMailbox.actionItemType || '...'}] action items from incoming emails` });
            items.push({ icon: '✅', text: 'Processes replies to existing action items as comments' });
        } else if (mode === 'ReplyOnly') {
            items.push({ icon: '✅', text: 'Processes replies to existing action items as comments' });
            items.push({ icon: '⛔', text: 'Does NOT create new items from standalone emails (they are skipped)' });
        } else {
            items.push({ icon: '⛔', text: 'No inbound email processing (outbound only)' });
        }

        if (editMailbox.outboundEnabled) {
            items.push({ icon: '✅', text: 'Sends notifications and updates from this address' });
        }

        if (editMailbox.autoResponseEnabled && mode === 'CreateAndReply') {
            items.push({ icon: '✅', text: 'Sends auto-response when a new item is created' });
        }

        if (mode !== 'Disabled') {
            const area = functionalAreaOptions.find(o => o.value === editMailbox.functionalArea);
            items.push({ icon: 'ℹ️', text: `Functional area: ${area?.label || editMailbox.functionalArea}` });
        }

        return items;
    };

    // ── Alias Management ────────────────────────────────────────────────────

    /**
     * Load aliases for a mailbox (called when opening an existing mailbox for edit)
     */
    const fetchAliases = async (mailboxId) => {
        try {
            const res = await apiFetch(`/api/EmailMailboxes/${mailboxId}/aliases`);
            const data = await res.json();
            if (data.success) {
                setAliases(data.data || []);
            }
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    /**
     * Save an alias (create or update)
     */
    const handleSaveAlias = async () => {
        if (!editAlias || !editMailbox?.id) return;
        if (!editAlias.aliasAddress?.trim()) {
            toast.warning('Please enter an alias email address');
            return;
        }
        setSavingAlias(true);
        try {
            const isNew = !editAlias.id;
            const url = isNew
                ? `/api/EmailMailboxes/${editMailbox.id}/aliases`
                : `/api/EmailMailboxes/${editMailbox.id}/aliases/${editAlias.id}`;
            const res = await apiFetch(url, {
                method: isNew ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editAlias)
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isNew ? 'Alias added' : 'Alias updated');
                setEditAlias(null);
                await fetchAliases(editMailbox.id);
            } else {
                toast.error(data.message || 'Failed to save alias');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error saving alias');
        } finally {
            setSavingAlias(false);
        }
    };

    /**
     * Delete an alias
     */
    const handleDeleteAlias = async (aliasId) => {
        if (!editMailbox?.id) return;
        if (!window.confirm('Are you sure you want to remove this alias?')) return;
        try {
            const res = await apiFetch(`/api/EmailMailboxes/${editMailbox.id}/aliases/${aliasId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success('Alias removed');
                await fetchAliases(editMailbox.id);
            } else {
                toast.error(data.message || 'Failed to remove alias');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Error removing alias');
        }
    };

    /**
     * Render form fields
     */
    const renderFormFields = () => {
        if (!editMailbox) return null;

        const currentMode = editMailbox.inboundMode || 'CreateAndReply';

        return (
            <div className="mailbox-form">
                {/* Behavior Summary */}
                <div className="behavior-summary">
                    <div className="summary-header">
                        <IoInformationCircleOutline size={18} />
                        <span>This mailbox will:</span>
                    </div>
                    <ul className="summary-list">
                        {getBehaviorSummary().map((item, i) => (
                            <li key={i}><span className="summary-icon">{item.icon}</span> {item.text}</li>
                        ))}
                    </ul>
                </div>

                {/* Basic Information */}
                <div className="form-section">
                    <h3>Basic Information</h3>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Display Name</label>
                            <input
                                type="text"
                                value={editMailbox.displayName || ''}
                                onChange={e => handleFieldChange('displayName', e.target.value)}
                                placeholder="e.g., IT Support Inbox"
                            />
                            <small className="field-help">Human-friendly label shown in the mailbox list and used in notifications (e.g., "IT Support Inbox")</small>
                        </div>
                        <div className="form-field">
                            <label>Email Address *</label>
                            <input
                                type="email"
                                value={editMailbox.emailAddress || ''}
                                onChange={e => handleFieldChange('emailAddress', e.target.value)}
                                placeholder="e.g., it@capland.co.za"
                                required
                            />
                            <small className="field-help">The actual email address CapTrac will monitor for inbound mail and/or send outbound mail from</small>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Functional Area *</label>
                            <select
                                value={editMailbox.functionalArea || 'General'}
                                onChange={e => handleFieldChange('functionalArea', e.target.value)}
                            >
                                {functionalAreaOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <small className="field-help">Determines which CapTrac module processes emails from this mailbox. "General" creates standard action items; "IT Service Management" creates ITSM tickets with SLA tracking; "Action Items" creates task-based items; "Asset Management" links to asset workflows; "Custom" allows free-form routing</small>
                        </div>
                        <div className="form-field">
                            <label>Purpose</label>
                            <input
                                type="text"
                                value={editMailbox.purpose || ''}
                                onChange={e => handleFieldChange('purpose', e.target.value)}
                                placeholder="e.g., IT helpdesk inbox for support tickets"
                            />
                            <small className="field-help">Internal description to help administrators understand what this mailbox is used for. Not visible to end users</small>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-field">
                            <label>Provider</label>
                            <select
                                value={editMailbox.provider || 'Microsoft365'}
                                onChange={e => handleFieldChange('provider', e.target.value)}
                            >
                                {providerOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <small className="field-help">Microsoft 365 uses the Graph API with Azure AD app credentials (recommended). IMAP connects to a traditional mail server using username/password</small>
                        </div>
                        <div className="form-field">
                            <label>Email Config Secret</label>
                            <select
                                value={editMailbox.emailConfigSecretId || ''}
                                onChange={e => handleFieldChange('emailConfigSecretId', e.target.value ? parseInt(e.target.value) : null)}
                            >
                                <option value="">Select email configuration...</option>
                                {emailConfigSecrets.map(secret => (
                                    <option key={secret.id} value={secret.id}>
                                        {secret.displayName || secret.secretKey}
                                    </option>
                                ))}
                            </select>
                            <small className="field-help">Links this mailbox to an App Secret of type "EmailConfig" that stores the Azure AD / SMTP credentials needed to authenticate. Create one under App Secrets if none are listed</small>
                        </div>
                    </div>
                    <div className="form-row checkboxes">
                        <div className="checkbox-with-help">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editMailbox.isActive !== false}
                                    onChange={e => handleFieldChange('isActive', e.target.checked)}
                                />
                                Active
                            </label>
                            <small className="field-help">When unchecked, this mailbox is disabled — no emails will be polled or sent</small>
                        </div>
                        <div className="checkbox-with-help">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={editMailbox.outboundEnabled === true}
                                    onChange={e => handleFieldChange('outboundEnabled', e.target.checked)}
                                />
                                Outbound Enabled
                            </label>
                            <small className="field-help">When enabled, CapTrac can send notification emails from this mailbox address (e.g., assignment notifications, status updates)</small>
                        </div>
                    </div>
                </div>

                {/* Inbound Mode — the core architectural selector */}
                <div className="form-section">
                    <h3>Inbound Email Handling</h3>
                    <small className="section-help">Choose how this mailbox processes incoming emails. This is the most important setting for the mailbox.</small>
                    <div className="inbound-mode-cards">
                        {inboundModeOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`mode-card ${currentMode === opt.value ? 'selected' : ''}`}
                                onClick={() => handleFieldChange('inboundMode', opt.value)}
                            >
                                <div className="mode-card-header">
                                    {opt.icon}
                                    <span className="mode-label">{opt.label}</span>
                                </div>
                                <p className="mode-description">{opt.description}</p>
                                <small className="mode-usecases">{opt.useCases}</small>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Inbound — Action Item Creation (only for CreateAndReply) */}
                {currentMode === 'CreateAndReply' && (
                    <div className="form-section">
                        <h3>Action Item Creation Rules</h3>
                        <small className="section-help">Configure how new action items are created from incoming emails.</small>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Action Item Type *</label>
                                <select
                                    value={editMailbox.actionItemType || ''}
                                    onChange={e => handleFieldChange('actionItemType', e.target.value)}
                                >
                                    <option value="">Select type...</option>
                                    {(() => {
                                        const itTypeKeys = new Set(itTicketTypes.map(t => t.typeKey));
                                        const filtered = actionItemTypes.filter(type => !itTypeKeys.has(type));
                                        const hasItTypes = itTicketTypes.length > 0;
                                        if (hasItTypes && !filtered.includes('IT')) filtered.push('IT');
                                        return filtered.map(type => {
                                            const info = getActionItemTypeInfo(type);
                                            return (
                                                <option key={type} value={type}>
                                                    {type}{info && info.recommended === false ? ' ⚠' : info && info.recommended === true ? ' ✓' : ''}
                                                </option>
                                            );
                                        });
                                    })()}
                                </select>
                                <small className="field-help">Determines how inbound emails are classified. Types marked ✓ are recommended for email inboxes; those marked ⚠ are typically system-generated and may not be appropriate</small>
                                {editMailbox.actionItemType && getActionItemTypeInfo(editMailbox.actionItemType) && (
                                    <small className={`field-help type-description ${getActionItemTypeInfo(editMailbox.actionItemType).recommended === false ? 'type-warning' : getActionItemTypeInfo(editMailbox.actionItemType).recommended === true ? 'type-recommended' : ''}`}>
                                        {getActionItemTypeInfo(editMailbox.actionItemType).description}
                                    </small>
                                )}
                            </div>
                            <div className="form-field">
                                <label>Template Key</label>
                                <input
                                    type="text"
                                    value={editMailbox.templateKey || ''}
                                    onChange={e => handleFieldChange('templateKey', e.target.value)}
                                    placeholder="Optional template key"
                                />
                                <small className="field-help">Optional key that links to a specific email template for formatting inbound-to-action-item mapping. Leave blank to use the default template for the selected action item type</small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Default Priority</label>
                                <select
                                    value={editMailbox.defaultPriority || 'Medium'}
                                    onChange={e => handleFieldChange('defaultPriority', e.target.value)}
                                >
                                    {priorityOptions.map(priority => (
                                        <option key={priority} value={priority}>{priority}</option>
                                    ))}
                                </select>
                                <small className="field-help">Priority level assigned to action items created from this mailbox. Users can change the priority later on individual items</small>
                            </div>
                            <div className="form-field">
                                <label>Default Property</label>
                                <select
                                    value={editMailbox.defaultPropertyId || ''}
                                    onChange={e => handleFieldChange('defaultPropertyId', e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">No default property</option>
                                    {properties.map(prop => (
                                        <option key={prop.propertyID} value={prop.propertyID}>{prop.propertyName}</option>
                                    ))}
                                </select>
                                <small className="field-help">Property automatically assigned to action items created from this mailbox. If not set, the item will have no default property and must be assigned manually</small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Default Department</label>
                                <select
                                    value={editMailbox.defaultDepartmentId || ''}
                                    onChange={e => handleFieldChange('defaultDepartmentId', e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">No default department</option>
                                    {departments.map(dept => (
                                        <option key={dept.departmentID} value={dept.departmentID}>{dept.departmentName}</option>
                                    ))}
                                </select>
                                <small className="field-help">Department automatically assigned to action items from this mailbox. Useful for routing items to the correct team (e.g., IT, Facilities, Finance)</small>
                            </div>
                            <div className="form-field">
                                <label>Default Assign To User</label>
                                <select
                                    value={editMailbox.defaultAssignToUserId || ''}
                                    onChange={e => handleFieldChange('defaultAssignToUserId', e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">Unassigned</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                    ))}
                                </select>
                                <small className="field-help">User automatically assigned as the responsible party for action items created from this mailbox. If unassigned, the item will appear unassigned until a user claims or is assigned to it</small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Default Function</label>
                                <select
                                    value={editMailbox.defaultFunctionId || ''}
                                    onChange={e => handleFieldChange('defaultFunctionId', e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">No default function</option>
                                    {functions.map(f => (
                                        <option key={f.functionID} value={f.functionID}>{f.functionName}</option>
                                    ))}
                                </select>
                                <small className="field-help">Functional area (e.g., Debtors, Creditors, IT Support) assigned to action items. Functions represent specific work responsibilities that can span across departments</small>
                            </div>
                            {/* ITSM-specific: ticket category shown alongside function */}
                            {editMailbox.functionalArea === 'ITServiceManagement' ? (
                                <div className="form-field">
                                    <label>Default Ticket Category</label>
                                    <select
                                        value={editMailbox.defaultTicketCategoryId || ''}
                                        onChange={e => handleFieldChange('defaultTicketCategoryId', e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">No default category</option>
                                        {itTicketTypes.filter(t => t.isActive).map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.displayName}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="field-help">IT Service Desk category for SLA policies, routing, and reporting</small>
                                </div>
                            ) : (
                                <div className="form-field" />
                            )}
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Default Board</label>
                                <select
                                    value={editMailbox.defaultBoardId || ''}
                                    onChange={e => handleFieldChange('defaultBoardId', e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">No default board (use claim rules)</option>
                                    {boards.filter(b => b.isActive !== false).map(b => (
                                        <option key={b.boardId} value={b.boardId}>{b.name}</option>
                                    ))}
                                </select>
                                <small className="field-help">Kanban board that action items created from this mailbox are automatically added to. Overrides board claim rules. Leave blank to let the system assign based on action item type.</small>
                            </div>
                            <div className="form-field" />
                        </div>
                    </div>
                )}

                {/* Reply Handling (for CreateAndReply and ReplyOnly) */}
                {currentMode !== 'Disabled' && (
                    <div className="form-section">
                        <h3>Reply Handling</h3>
                        <small className="section-help">Configure how email replies to existing action item threads are processed.</small>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Reply Action</label>
                                <select
                                    value={editMailbox.replyAction || 'AddComment'}
                                    onChange={e => handleFieldChange('replyAction', e.target.value)}
                                >
                                    {replyActionOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <small className="field-help">"Add as comment" appends the reply to the existing action item thread (recommended). "Create new" always creates a separate action item, even for replies to existing threads</small>
                            </div>
                            <div className="form-field" />
                        </div>
                    </div>
                )}

                {/* Outbound Configuration */}
                {editMailbox.outboundEnabled && (
                    <div className="form-section">
                        <h3>Outbound Email</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>From Display Name</label>
                                <input
                                    type="text"
                                    value={editMailbox.outboundFromName || ''}
                                    onChange={e => handleFieldChange('outboundFromName', e.target.value)}
                                    placeholder="Defaults to mailbox display name"
                                />
                                <small className="field-help">The sender name that appears in the recipient's inbox (e.g., "CapTrac IT Support"). If left blank, the mailbox Display Name is used</small>
                            </div>
                            <div className="form-field">
                                <label>Outbound Email Template</label>
                                <select
                                    value={editMailbox.outboundTemplateId || ''}
                                    onChange={e => handleFieldChange('outboundTemplateId', e.target.value ? parseInt(e.target.value) : null)}
                                >
                                    <option value="">Default CapTrac template</option>
                                    {emailTemplates.map(t => (
                                        <option key={t.emailTemplateId} value={t.emailTemplateId}>
                                            {t.displayName}
                                        </option>
                                    ))}
                                </select>
                                <small className="field-help">HTML email template used when sending notifications from this mailbox (e.g., assignment alerts, status updates). Configure templates under Email Templates settings</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* Auto-Response Configuration (only for CreateAndReply — ReplyOnly doesn't create items) */}
                {currentMode === 'CreateAndReply' && (
                    <div className="form-section">
                        <h3>Auto-Response</h3>
                        <div className="form-row checkboxes">
                            <div className="checkbox-with-help">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={editMailbox.autoResponseEnabled === true}
                                        onChange={e => handleFieldChange('autoResponseEnabled', e.target.checked)}
                                    />
                                    Send auto-response when a new item is created from an inbound email
                                </label>
                                <small className="field-help">When enabled, the sender receives an automatic acknowledgment email confirming their message was received and an action item was created</small>
                            </div>
                        </div>
                        {editMailbox.autoResponseEnabled && (
                            <>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Auto-Response Subject</label>
                                        <input
                                            type="text"
                                            value={editMailbox.autoResponseSubject || ''}
                                            onChange={e => handleFieldChange('autoResponseSubject', e.target.value)}
                                            placeholder="Re: {TicketTitle} [#{TicketId}]"
                                        />
                                        <small className="field-help">Subject line for the auto-response email. Use placeholders to include dynamic data: {'{TicketId}'} (item number), {'{TicketTitle}'} (email subject), {'{SenderName}'} (sender's name), {'{FunctionalArea}'} (module name)</small>
                                    </div>
                                    <div className="form-field">
                                        <label>Auto-Response Template</label>
                                        <select
                                            value={editMailbox.autoResponseTemplateId || ''}
                                            onChange={e => handleFieldChange('autoResponseTemplateId', e.target.value ? parseInt(e.target.value) : null)}
                                        >
                                            <option value="">Default auto-response template</option>
                                            {emailTemplates.map(t => (
                                                <option key={t.emailTemplateId} value={t.emailTemplateId}>
                                                    {t.displayName}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="field-help">HTML email template for the acknowledgment reply. Configure templates under Email Templates settings. If not set, a built-in default acknowledgment template is used</small>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Processing Options (shown for any non-Disabled mode) */}
                {currentMode !== 'Disabled' && (
                    <div className="form-section">
                        <h3>Processing Options</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Folder to Monitor</label>
                                {mailFolders.length > 0 ? (
                                    <select
                                        value={editMailbox.folderToMonitor || 'INBOX'}
                                        onChange={e => handleFieldChange('folderToMonitor', e.target.value)}
                                    >
                                        {/* Ensure current value is always an option even if not in the fetched list */}
                                        {editMailbox.folderToMonitor && !mailFolders.some(f => f.displayName === editMailbox.folderToMonitor) && (
                                            <option value={editMailbox.folderToMonitor}>{editMailbox.folderToMonitor}</option>
                                        )}
                                        {mailFolders.map(f => (
                                            <option key={f.id} value={f.displayName}>
                                                {f.displayName} ({f.totalItemCount})
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={editMailbox.folderToMonitor || 'INBOX'}
                                        onChange={e => handleFieldChange('folderToMonitor', e.target.value)}
                                        placeholder="INBOX"
                                    />
                                )}
                                <small className="field-help">
                                    {loadingFolders ? 'Loading folders...' :
                                     mailFolders.length > 0 ? 'Select the email folder to monitor for incoming messages' :
                                     isCreateMode ? 'Save the mailbox first to detect available folders' :
                                     'The email folder CapTrac monitors for unread messages. Use "INBOX" for the primary inbox'}
                                </small>
                            </div>
                            <div className="form-field">
                                <label>Processed Folder</label>
                                {mailFolders.length > 0 ? (
                                    <select
                                        value={editMailbox.processedFolder || ''}
                                        onChange={e => handleFieldChange('processedFolder', e.target.value || null)}
                                    >
                                        <option value="">None (keep in place)</option>
                                        {/* Ensure current value is always an option */}
                                        {editMailbox.processedFolder && !mailFolders.some(f => f.displayName === editMailbox.processedFolder) && (
                                            <option value={editMailbox.processedFolder}>{editMailbox.processedFolder}</option>
                                        )}
                                        {mailFolders.map(f => (
                                            <option key={f.id} value={f.displayName}>
                                                {f.displayName} ({f.totalItemCount})
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={editMailbox.processedFolder || ''}
                                        onChange={e => handleFieldChange('processedFolder', e.target.value)}
                                        placeholder="Optional: move processed emails here"
                                    />
                                )}
                                <small className="field-help">
                                    {mailFolders.length > 0 ? 'Select a folder to move processed emails to, or leave as "None" to keep them in place' :
                                     'After processing, emails are moved to this folder. Leave empty to keep emails in the monitored folder and mark them as read instead'}
                                </small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Polling Interval (minutes)</label>
                                <input
                                    type="number"
                                    value={editMailbox.pollingIntervalMinutes || ''}
                                    onChange={e => handleFieldChange('pollingIntervalMinutes', e.target.value ? parseInt(e.target.value) : null)}
                                    placeholder="Default: 2"
                                    min={1}
                                />
                                <small className="field-help">How often CapTrac checks this mailbox for new emails. Lower values mean faster processing but more API calls. Default is 2 minutes. Minimum is 1 minute</small>
                            </div>
                            <div className="form-field" />
                        </div>
                        <div className="form-row checkboxes">
                            <div className="checkbox-with-help">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={editMailbox.markAsReadAfterProcessing !== false}
                                        onChange={e => handleFieldChange('markAsReadAfterProcessing', e.target.checked)}
                                    />
                                    Mark as read after processing
                                </label>
                                <small className="field-help">When enabled, processed emails are marked as read so they won't be picked up again. Disable only if you use the "Processed Folder" option to move emails instead</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* Email Aliases — only shown for existing (saved) mailboxes with inbound enabled */}
                {!isCreateMode && editMailbox.id && currentMode === 'CreateAndReply' && (
                    <div className="form-section alias-section">
                        <h3>
                            <IoMail size={18} /> Email Aliases
                        </h3>
                        <small className="section-help">
                            Aliases let a single mailbox receive emails on multiple addresses and route them to different action item types.
                            For example, if this mailbox is <strong>{editMailbox.emailAddress}</strong>, you can add an alias like <strong>dev@{editMailbox.emailAddress?.split('@')[1] || 'example.com'}</strong> to
                            route those emails to "Software Dev Request" instead of the default "{editMailbox.actionItemType || '...'}" type.
                            <br /><br />
                            <strong>Important:</strong> The alias must be configured as a valid alias in your mail server (e.g., Microsoft 365 / Exchange) so that emails sent to the alias address arrive in this mailbox's inbox.
                        </small>

                        {/* Existing Aliases List */}
                        {aliases.length > 0 && (
                            <div className="alias-list">
                                {aliases.map(alias => (
                                    <div key={alias.id} className={`alias-card ${alias.isActive ? '' : 'inactive'}`}>
                                        <div className="alias-card-header">
                                            <div className="alias-info">
                                                <strong>{alias.displayName}</strong>
                                                <span className="alias-email">{alias.aliasAddress}</span>
                                            </div>
                                            <div className="alias-badges">
                                                {alias.actionItemType && (
                                                    <span className="alias-type-badge">{alias.actionItemType}</span>
                                                )}
                                                {!alias.isActive && (
                                                    <span className="alias-inactive-badge">Disabled</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="alias-card-actions">
                                            <button
                                                className="btn-alias-edit"
                                                onClick={() => setEditAlias({ ...alias })}
                                                title="Edit alias"
                                            >
                                                <IoSettings size={14} /> Edit
                                            </button>
                                            <button
                                                className="btn-alias-delete"
                                                onClick={() => handleDeleteAlias(alias.id)}
                                                title="Remove alias"
                                            >
                                                <IoTrash size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {aliases.length === 0 && !editAlias && (
                            <div className="alias-empty">
                                <p>No aliases configured. All emails to this mailbox will use the default settings above.</p>
                            </div>
                        )}

                        {/* Add Alias Button */}
                        {!editAlias && (
                            <button
                                className="btn-add-alias"
                                onClick={() => setEditAlias({
                                    aliasAddress: '',
                                    displayName: '',
                                    isActive: true,
                                    actionItemType: '',
                                    templateKey: '',
                                    defaultPriority: '',
                                    defaultDepartmentId: null,
                                    defaultAssignToUserId: null,
                                    defaultFunctionId: null,
                                    defaultTicketCategoryId: null,
                                    defaultBoardId: null
                                })}
                            >
                                <IoAdd size={16} /> Add Alias
                            </button>
                        )}

                        {/* Alias Edit Form (inline) */}
                        {editAlias && (
                            <div className="alias-edit-form">
                                <h4>{editAlias.id ? 'Edit Alias' : 'Add New Alias'}</h4>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Alias Email Address *</label>
                                        <input
                                            type="email"
                                            value={editAlias.aliasAddress || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, aliasAddress: e.target.value }))}
                                            placeholder={`e.g. dev@${editMailbox.emailAddress?.split('@')[1] || 'example.com'}`}
                                        />
                                        <small className="field-help">
                                            The email alias address. This must be set up as a valid alias on your mail server (Microsoft 365 / Exchange).
                                            Emails sent to this address will arrive in the {editMailbox.emailAddress} inbox.
                                        </small>
                                    </div>
                                    <div className="form-field">
                                        <label>Display Name</label>
                                        <input
                                            type="text"
                                            value={editAlias.displayName || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, displayName: e.target.value }))}
                                            placeholder="e.g. Software Dev Requests"
                                        />
                                        <small className="field-help">A friendly name to identify this alias in the admin interface</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Action Item Type (Override)</label>
                                        <select
                                            value={editAlias.actionItemType || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, actionItemType: e.target.value || null }))}
                                        >
                                            <option value="">— Use mailbox default ({editMailbox.actionItemType || 'none'}) —</option>
                                            {actionItemTypes.map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                        <small className="field-help">
                                            When an email is addressed to this alias, the action item will be created with this type instead of the mailbox's default.
                                            Leave blank to use the mailbox default.
                                        </small>
                                    </div>
                                    <div className="form-field">
                                        <label>Priority (Override)</label>
                                        <select
                                            value={editAlias.defaultPriority || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, defaultPriority: e.target.value || null }))}
                                        >
                                            <option value="">— Use mailbox default ({editMailbox.defaultPriority || 'Medium'}) —</option>
                                            {priorityOptions.map(p => (
                                                <option key={p} value={p}>{p}</option>
                                            ))}
                                        </select>
                                        <small className="field-help">Override the priority for action items created from this alias</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Assign to Department (Override)</label>
                                        <select
                                            value={editAlias.defaultDepartmentId || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, defaultDepartmentId: e.target.value ? parseInt(e.target.value) : null }))}
                                        >
                                            <option value="">— Use mailbox default —</option>
                                            {departments.map(d => (
                                                <option key={d.departmentID} value={d.departmentID}>{d.departmentName}</option>
                                            ))}
                                        </select>
                                        <small className="field-help">Override which department gets assigned action items from this alias</small>
                                    </div>
                                    <div className="form-field">
                                        <label>Assign to User (Override)</label>
                                        <select
                                            value={editAlias.defaultAssignToUserId || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, defaultAssignToUserId: e.target.value ? parseInt(e.target.value) : null }))}
                                        >
                                            <option value="">— Use mailbox default —</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>
                                                    {u.email ? `${u.name} (${u.email})` : u.name}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="field-help">Override which user gets assigned action items from this alias</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <label>Default Board (Override)</label>
                                        <select
                                            value={editAlias.defaultBoardId || ''}
                                            onChange={e => setEditAlias(prev => ({ ...prev, defaultBoardId: e.target.value ? parseInt(e.target.value) : null }))}
                                        >
                                            <option value="">— Auto-resolve from type —</option>
                                            {boards.map(b => (
                                                <option key={b.boardId} value={b.boardId}>{b.name}</option>
                                            ))}
                                        </select>
                                        <small className="field-help">Override which Kanban board receives action items from this alias. If unset, the board is resolved from the action item type</small>
                                    </div>
                                    <div className="form-field" />
                                </div>
                                <div className="form-row checkboxes">
                                    <div className="checkbox-with-help">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={editAlias.isActive !== false}
                                                onChange={e => setEditAlias(prev => ({ ...prev, isActive: e.target.checked }))}
                                            />
                                            Active
                                        </label>
                                        <small className="field-help">When unchecked, this alias is ignored during email processing</small>
                                    </div>
                                </div>
                                <div className="alias-form-actions">
                                    <button className="btn-cancel" onClick={() => setEditAlias(null)}>Cancel</button>
                                    <button
                                        className="btn-save"
                                        onClick={handleSaveAlias}
                                        disabled={savingAlias || !editAlias.aliasAddress?.trim()}
                                    >
                                        <IoSave size={14} />
                                        {savingAlias ? 'Saving...' : (editAlias.id ? 'Update Alias' : 'Add Alias')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* IMAP Settings (only shown when provider is IMAP) */}
                {editMailbox.provider === 'IMAP' && (
                    <div className="form-section">
                        <h3>IMAP Server Settings</h3>
                        <div className="form-row">
                            <div className="form-field">
                                <label>IMAP Server</label>
                                <input
                                    type="text"
                                    value={editMailbox.imapServer || ''}
                                    onChange={e => handleFieldChange('imapServer', e.target.value)}
                                    placeholder="imap.example.com"
                                />
                                <small className="field-help">Hostname or IP address of the IMAP mail server (e.g., imap.gmail.com, mail.example.com)</small>
                            </div>
                            <div className="form-field">
                                <label>Port</label>
                                <input
                                    type="number"
                                    value={editMailbox.imapPort || 993}
                                    onChange={e => {
                                        const value = parseInt(e.target.value, 10);
                                        handleFieldChange('imapPort', isNaN(value) ? 993 : value);
                                    }}
                                    min={1}
                                    max={65535}
                                />
                                <small className="field-help">IMAP port number. Standard ports: 993 (SSL/TLS, recommended) or 143 (unencrypted / STARTTLS)</small>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <label>Username</label>
                                <input
                                    type="text"
                                    value={editMailbox.imapUsername || ''}
                                    onChange={e => handleFieldChange('imapUsername', e.target.value)}
                                />
                                <small className="field-help">IMAP login username, typically the full email address (e.g., support@example.com)</small>
                            </div>
                            <div className="form-field">
                                <label>Password</label>
                                <input
                                    type="password"
                                    value={editMailbox.imapPassword || ''}
                                    onChange={e => handleFieldChange('imapPassword', e.target.value)}
                                />
                                <small className="field-help">IMAP login password or app-specific password. For Gmail/Microsoft, you may need to generate an app password</small>
                            </div>
                        </div>
                        <div className="form-row checkboxes">
                            <div className="checkbox-with-help">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={editMailbox.imapUseSsl !== false}
                                        onChange={e => handleFieldChange('imapUseSsl', e.target.checked)}
                                    />
                                    Use SSL/TLS
                                </label>
                                <small className="field-help">Enable encrypted connection to the IMAP server. Always recommended for security. Disable only if your server does not support SSL/TLS</small>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="email-mailboxes-page">
<div className="page-header">
                <div className="header-content">
                    <h1><IoMail size={28} /> Email Mailboxes</h1>
                    <p className="subtitle">Configure email accounts for inbound and outbound processing across functional areas</p>
                </div>
                <button className="btn-create" onClick={handleCreateClick}>
                    <IoAdd size={20} /> Add Mailbox
                </button>
            </div>

            {loading ? (
                <div className="loading">Loading mailboxes...</div>
            ) : (
                <div className="mailboxes-list">
                    {mailboxes.length === 0 ? (
                        <div className="empty-state">
                            <IoMail size={64} />
                            <h3>No mailboxes configured</h3>
                            <p>Add a mailbox to start processing incoming emails and creating action items automatically.</p>
                            <button className="btn-create" onClick={handleCreateClick}>
                                <IoAdd size={20} /> Add Your First Mailbox
                            </button>
                        </div>
                    ) : (
                        <div className="mailboxes-grid">
                            {mailboxes.map(mailbox => (
                                <div
                                    key={mailbox.id}
                                    className={`mailbox-card ${mailbox.isActive ? 'active' : 'inactive'}`}
                                    onClick={() => handleMailboxClick(mailbox)}
                                >
                                    <div className="card-header">
                                        <div className="mailbox-info">
                                            <h3>{mailbox.displayName || mailbox.emailAddress}</h3>
                                            <span className="email-address">{mailbox.emailAddress}</span>
                                        </div>
                                        <div className="status-badge">
                                            {mailbox.isActive ? (
                                                <span className="badge active"><IoCheckmarkCircle /> Active</span>
                                            ) : (
                                                <span className="badge inactive"><IoPauseCircle /> Inactive</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="card-body">
                                        <div className="info-row">
                                            <span className="label">Functional Area:</span>
                                            <span className="value">
                                                {functionalAreaOptions.find(o => o.value === mailbox.functionalArea)?.label || mailbox.functionalArea}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">Inbound Mode:</span>
                                            <span className="value">
                                                {mailbox.inboundMode === 'CreateAndReply' && <span className="direction-badge inbound">Create & Reply</span>}
                                                {mailbox.inboundMode === 'ReplyOnly' && <span className="direction-badge reply-only">Reply Only</span>}
                                                {(mailbox.inboundMode === 'Disabled' || (!mailbox.inboundMode && !mailbox.inboundEnabled)) && <span className="direction-badge none">Disabled</span>}
                                                {!mailbox.inboundMode && mailbox.inboundEnabled && <span className="direction-badge inbound">Create & Reply</span>}
                                                {mailbox.outboundEnabled && <span className="direction-badge outbound">Outbound</span>}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">Action Item Type:</span>
                                            <span className="value">{mailbox.actionItemType}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">Last Checked:</span>
                                            <span className="value">{formatDate(mailbox.lastCheckedAt)}</span>
                                        </div>
                                        {mailbox.autoResponseEnabled && (
                                            <div className="info-row">
                                                <span className="label">Auto-Response:</span>
                                                <span className="value badge active">Enabled</span>
                                            </div>
                                        )}
                                        {mailbox.lastError && (
                                            <div className="error-row">
                                                <IoAlertCircle /> {mailbox.lastError}
                                            </div>
                                        )}
                                    </div>

                                    <div className="card-actions">
                                        <button
                                            className="btn-action"
                                            onClick={(e) => handleViewStats(mailbox, e)}
                                            title="View Statistics"
                                        >
                                            <IoStatsChart size={18} />
                                        </button>
                                        <button
                                            className="btn-action"
                                            onClick={(e) => handleTriggerCheck(mailbox, e)}
                                            title="Check Now"
                                        >
                                            <IoSync size={18} />
                                        </button>
                                        <button
                                            className={`btn-action ${mailbox.isActive ? 'pause' : 'play'}`}
                                            onClick={(e) => handleToggleActive(mailbox, e)}
                                            title={mailbox.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            {mailbox.isActive ? <IoPauseCircle size={18} /> : <IoCheckmarkCircle size={18} />}
                                        </button>
                                        <button
                                            className="btn-action settings"
                                            onClick={() => handleMailboxClick(mailbox)}
                                            title="Edit Settings"
                                        >
                                            <IoSettings size={18} />
                                        </button>
                                        <button
                                            className="btn-action delete"
                                            onClick={(e) => handleDeleteClick(mailbox, e)}
                                            title="Delete"
                                        >
                                            <IoTrash size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Preset Selector Modal (shown when creating before form) */}
            {isCreateMode && !editMailbox && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add Mailbox — Choose a Purpose</h2>
                            <button className="close-btn" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p className="preset-intro">Select a purpose to get started with recommended settings. You can customize everything afterwards.</p>
                            <div className="preset-cards">
                                {mailboxPresets.map(preset => (
                                    <div
                                        key={preset.id}
                                        className="preset-card"
                                        onClick={() => handlePresetSelect(preset)}
                                    >
                                        <div className="preset-icon">{preset.icon}</div>
                                        <h3>{preset.label}</h3>
                                        <p className="preset-description">{preset.description}</p>
                                        <small className="preset-examples">{preset.examples}</small>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit/Create Form Modal */}
            {editMailbox && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isCreateMode ? 'Add Mailbox' : 'Edit Mailbox'}</h2>
                            <button className="close-btn" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {renderFormFields()}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                            <button
                                className="btn-save"
                                onClick={handleSave}
                                disabled={saving || !editMailbox.emailAddress}
                            >
                                <IoSave size={18} />
                                {saving ? 'Saving...' : 'Save Mailbox'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteTarget && (
                <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="modal-content small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Delete Mailbox</h2>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete <strong>{deleteTarget.displayName || deleteTarget.emailAddress}</strong>?</p>
                            <p className="warning">This will stop all email processing for this mailbox. Existing action items will not be affected.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="btn-delete" onClick={handleDeleteConfirm}>
                                <IoTrash size={18} /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Modal */}
            {statsMailbox && (
                <div className="modal-overlay" onClick={closeStatsModal}>
                    <div className="modal-content large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <IoStatsChart size={24} />
                                Statistics: {statsMailbox.displayName || statsMailbox.emailAddress}
                            </h2>
                            <button className="close-btn" onClick={closeStatsModal}>&times;</button>
                        </div>
                        <div className="modal-body stats-modal">
                            {loadingStats ? (
                                <div className="loading">Loading statistics...</div>
                            ) : (
                                <>
                                    {/* Stats Overview */}
                                    {stats && (
                                        <div className="stats-grid">
                                            <div className="stat-card">
                                                <div className="stat-value">{stats.totalProcessed}</div>
                                                <div className="stat-label">Total Processed</div>
                                            </div>
                                            <div className="stat-card success">
                                                <div className="stat-value">{stats.actionItemsCreated}</div>
                                                <div className="stat-label">Action Items Created</div>
                                            </div>
                                            <div className="stat-card info">
                                                <div className="stat-value">{stats.commentsAdded}</div>
                                                <div className="stat-label">Comments Added</div>
                                            </div>
                                            <div className="stat-card error">
                                                <div className="stat-value">{stats.errors}</div>
                                                <div className="stat-label">Errors</div>
                                            </div>
                                            <div className="stat-card">
                                                <div className="stat-value">{stats.processedLast24Hours}</div>
                                                <div className="stat-label">Last 24 Hours</div>
                                            </div>
                                            <div className="stat-card">
                                                <div className="stat-value">{stats.processedLast7Days}</div>
                                                <div className="stat-label">Last 7 Days</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Status Info */}
                                    {stats && (
                                        <div className="status-info">
                                            <div className="info-item">
                                                <IoTime size={18} />
                                                <span>Last checked: {formatDate(stats.lastCheckedAt)}</span>
                                            </div>
                                            {stats.lastError && (
                                                <div className="info-item error">
                                                    <IoAlertCircle size={18} />
                                                    <span>Last error: {stats.lastError}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Processed Emails */}
                                    <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'processed' ? null : 'processed')}>
                                        {expandedSection === 'processed' ? <IoChevronDown /> : <IoChevronForward />}
                                        <h3>Recent Processed Emails ({processedEmails.length})</h3>
                                    </div>
                                    
                                    {expandedSection === 'processed' && (
                                        <div className="processed-emails-table">
                                            {processedEmails.length === 0 ? (
                                                <p className="no-data">No processed emails yet</p>
                                            ) : (
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>From</th>
                                                            <th>Subject</th>
                                                            <th>Conversation ID</th>
                                                            <th>Received</th>
                                                            <th>Result</th>
                                                            <th>Action Item</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {processedEmails.map(email => (
                                                            <tr key={email.id}>
                                                                <td className="email-from">{email.fromEmail}</td>
                                                                <td className="email-subject">{email.subject}</td>
                                                                <td className="conversation-id">
                                                                    {email.conversationId ? (
                                                                        <code title={email.conversationId} className="conv-id-badge">
                                                                            {email.conversationId.substring(0, 12)}...
                                                                        </code>
                                                                    ) : (
                                                                        <span className="no-conv-id">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="email-date">{formatDate(email.receivedAt)}</td>
                                                                <td>
                                                                    <span className={`result-badge ${getResultBadgeClass(email.processingResult)}`}>
                                                                        {email.processingResult}
                                                                    </span>
                                                                </td>
                                                                <td className="action-item-id">
                                                                    {email.createdActionItemId && (
                                                                        <a href={`/action-items/${email.createdActionItemId}`} target="_blank" rel="noopener noreferrer">
                                                                            #{email.createdActionItemId}
                                                                        </a>
                                                                    )}
                                                                    {email.commentedOnActionItemId && (
                                                                        <a href={`/action-items/${email.commentedOnActionItemId}`} target="_blank" rel="noopener noreferrer">
                                                                            #{email.commentedOnActionItemId}
                                                                        </a>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeStatsModal}>Close</button>
                            <button className="btn-refresh" onClick={(e) => handleViewStats(statsMailbox, e)}>
                                <IoRefresh size={18} /> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailMailboxesPage;
